import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase, db } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Store, storesService } from '@/services/stores'
import { createLogger } from '@/lib/logger'
import { prefetchDashboardData } from '@/lib/dashboard-v2/prefetchDashboardQueries'
import { appFetch, isAppApiConfigured } from '@/lib/appApi'
import type { UserProfile } from '@/hooks/useProfile'

const log = createLogger('store-context')

type DashboardBootstrapResponse = {
    ok: boolean
    profile: (UserProfile & { id?: string; current_store_id?: string | null }) | null
    stores: Store[]
    activeStoreId: string | null
}

// ============================================================
// StoreContext — Multi-Store Foundation (Step 3)
// ============================================================
// Provides the "active store" to all dashboard pages.
// Replaces the old useMyStore() → .single() pattern.
//
// Backward-compatible: users with 1 store see no difference.
// The active store is persisted via profiles.current_store_id.
//
// FALLBACK RULES:
//   - current_store_id is NULL → auto-select first store, persist silently
//   - current_store_id points to deleted/invalid store → same fallback
//   - User has zero stores → activeStore = null, loading = false
//   - Persist is fire-and-forget (never blocks render)
//   - loadStores is guarded by ref to prevent concurrent calls
// ============================================================

interface StoreContextType {
    /** The currently selected store */
    activeStore: Store | null
    /** Boutiques accessibles (propriétaire ou membre d'équipe) */
    stores: Store[]
    /** Loading state */
    loading: boolean
    /** True when the last load attempt ended with a network/API error (not "no stores") */
    loadError: boolean
    /** Whether the active store is suspended/terminated */
    isSuspended: boolean
    /** Switch to a different store */
    switchStore: (storeId: string) => Promise<void>
    /** Refresh stores list (e.g. after creating a new store) */
    refreshStores: () => Promise<void>
}

export const StoreContext = createContext<StoreContextType | undefined>(undefined)

export function StoreProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth()
    const queryClient = useQueryClient()
    const [stores, setStores] = useState<Store[]>([])
    const [activeStore, setActiveStore] = useState<Store | null>(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)

    // Guard: one in-flight loadStores; queue another if refresh requested while loading (avoids dropped refresh after PATCH).
    const loadingRef = useRef(false)
    const pendingLoadRef = useRef(false)
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const isAbortError = (err: unknown): boolean => {
        const anyErr = err as any
        const msg = String(anyErr?.message || anyErr?.details || '')
        return msg.includes('AbortError') || msg.includes('signal is aborted')
    }

    // Persist current_store_id silently (fire-and-forget, never blocks UI)
    const persistActiveStoreId = useCallback((storeId: string) => {
        if (!user) return
        db
            .from('profiles')
            .update({ current_store_id: storeId })
            .eq('id', user.id)
            .then(({ error }) => {
                if (!mountedRef.current) return
                if (error) {
                    if (isAbortError(error)) return
                    log.warn('Failed to persist current_store_id', error)
                }
            })
            .catch((err) => {
                // Extra safety: never surface AbortError noise in Sentry/console
                if (!mountedRef.current) return
                if (isAbortError(err)) return
                log.warn('Failed to persist current_store_id', err)
            })
    }, [user])

    // Load all user stores + resolve active store
    const loadStores = useCallback(async () => {
        if (!user) {
            if (mountedRef.current) {
                setStores([])
                setActiveStore(null)
                setLoading(false)
            }
            return
        }

        if (loadingRef.current) {
            pendingLoadRef.current = true
            return
        }
        loadingRef.current = true

        log.debug('Loading stores for user', user.id)

        let wasAborted = false

        try {
            let list: Store[] = []
            let currentId: string | null = null
            let profileForCache: UserProfile | null = null

            if (isAppApiConfigured()) {
                try {
                    const bootstrap = await appFetch<DashboardBootstrapResponse>('/api/app/dashboard-bootstrap')
                    list = Array.isArray(bootstrap.stores) ? bootstrap.stores : []
                    currentId = bootstrap.activeStoreId ?? bootstrap.profile?.current_store_id ?? null
                    profileForCache = bootstrap.profile
                        ? {
                            full_name: bootstrap.profile.full_name ?? null,
                            business_name: bootstrap.profile.business_name ?? null,
                            avatar_url: bootstrap.profile.avatar_url ?? null,
                        }
                        : null
                } catch (err) {
                    if (isAbortError(err)) throw err
                    log.warn('Dashboard bootstrap failed; falling back to split store/profile load', err)
                }
            }

            if (list.length === 0 && currentId === null && profileForCache === null) {
                // Fallback for local/dev BFF outages. Production normally uses the
                // bootstrap endpoint so the dashboard avoids the extra round-trip.
                const [fallbackList, profileResult] = await Promise.all([
                    storesService.listAll(),
                    db.from('profiles').select('current_store_id').eq('id', user.id).maybeSingle(),
                ])
                list = fallbackList
                const { data: profile, error: profileError } = profileResult
                if (profileError && !isAbortError(profileError)) {
                    log.warn('Failed to load profile for store selection', profileError)
                }
                currentId = profile?.current_store_id ?? null
            }

            if (!mountedRef.current) return

            let nextActive: Store | null = null
            if (currentId) {
                nextActive = list.find(s => s.id === currentId) ?? null
            }
            if (!nextActive && list.length > 0) {
                nextActive = list[0]!
                persistActiveStoreId(nextActive.id)
            }

            if (mountedRef.current) {
                setStores(list)
                setActiveStore(nextActive)
                setLoadError(false)

                if (user?.id && profileForCache) {
                    const snapshot: UserProfile = {
                        full_name: profileForCache.full_name,
                        business_name: profileForCache.business_name,
                        avatar_url: profileForCache.avatar_url,
                    }
                    queryClient.setQueryData(['profile', user.id], snapshot)
                }

                // Prefetch critical dashboard data in the background while the rest
                // of the app renders. By the time DashboardHome mounts its hooks,
                // the data will already be in the React Query cache.
                if (nextActive?.id) {
                    prefetchDashboardData(queryClient, nextActive.id)
                }
            }
        } catch (err) {
            if (isAbortError(err)) { wasAborted = true; return }
            log.warn('Failed to load stores', err)
            if (mountedRef.current) {
                setStores([])
                setActiveStore(null)
                setLoadError(true)
            }
        } finally {
            const scheduleFollowUp = pendingLoadRef.current
            pendingLoadRef.current = false
            loadingRef.current = false

            if (wasAborted) {
                // CRITICAL: Do NOT set loading=false on abort.
                // stores[] is still empty from initial state; setting loading=false
                // would make StoreRequiredGuard think "zero stores" and redirect to onboarding.
                // Schedule a retry instead.
                log.debug('Store load aborted — scheduling retry')
                if (mountedRef.current) {
                    setTimeout(() => {
                        if (mountedRef.current) void loadStores()
                    }, 300)
                }
            } else {
                if (mountedRef.current) setLoading(false)
                if (scheduleFollowUp && mountedRef.current && user) {
                    queueMicrotask(() => {
                        if (mountedRef.current && user) void loadStores()
                    })
                }
            }
        }
    }, [user, persistActiveStoreId, queryClient])

    // Load on mount and when user changes
    useEffect(() => {
        loadStores()
    }, [loadStores])

    // Auto-refresh when stores-changed event is fired (e.g. from Onboarding)
    // or when tab regains focus (catches stale data)
    useEffect(() => {
        const handleStoresChanged = () => {
            loadingRef.current = false // Reset guard to allow reload
            loadStores()
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                loadingRef.current = false
                loadStores()
            }
        }
        window.addEventListener('stores-changed', handleStoresChanged)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => {
            window.removeEventListener('stores-changed', handleStoresChanged)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [loadStores])

    // Switch active store
    const switchStore = useCallback(async (storeId: string) => {
        const target = stores.find(s => s.id === storeId)
        if (!target || !user) return

        // 1. Update local state immediately (optimistic)
        setActiveStore(target)

        // 2. Persist to DB
        await db
            .from('profiles')
            .update({ current_store_id: storeId })
            .eq('id', user.id)

        // 3. Invalidate all store-dependent queries
        queryClient.invalidateQueries({ queryKey: ['store'] })
        queryClient.invalidateQueries({ queryKey: ['products'] })
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        queryClient.invalidateQueries({ queryKey: ['customers'] })
        queryClient.invalidateQueries({ queryKey: ['analytics'] })
    }, [stores, user, queryClient])

    // Refresh stores list
    const refreshStores = useCallback(async () => {
        await loadStores()
    }, [loadStores])

    const isSuspended = activeStore ? (activeStore as any).admin_status !== 'active' && (activeStore as any).admin_status != null : false

    return (
        <StoreContext.Provider value={{
            activeStore,
            stores,
            loading,
            loadError,
            isSuspended,
            switchStore,
            refreshStores,
        }}>
            {children}
        </StoreContext.Provider>
    )
}

/**
 * Access the active store and store switching capability.
 * Use this instead of useMyStore() in all dashboard pages.
 */
export function useActiveStore() {
    const context = useContext(StoreContext)
    if (context === undefined) {
        throw new Error('useActiveStore must be used within a StoreProvider')
    }
    return context
}

/**
 * Optional store context — returns undefined when used outside StoreProvider.
 * Use on public storefront pages (e.g. CategoryProductsPage) where StoreProvider is not mounted.
 */
export function useOptionalActiveStore(): StoreContextType | undefined {
    return useContext(StoreContext)
}
